/**
 * Hooks de TanStack Query para el módulo de Límites BO.
 *
 * @taskId T478
 * @see functional/Specs/specs_cycle_4.md HU-28-FE
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  assignMerchantToRule,
  createLimitRule,
  getLimitRule,
  getLimitRuleMerchants,
  getLimitRules,
  toggleLimitRuleStatus,
  unassignMerchantFromRule,
  updateLimitRule,
} from '../../../api/limits.api';
import type {
  AssignMerchantPayload,
  CreateLimitRulePayload,
  LimitAssignment,
  LimitRule,
  LimitRuleListParams,
  LimitRuleListResponse,
  MerchantAssignmentSummary,
  UpdateLimitRulePayload,
} from '../types/limit.types';

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const limitKeys = {
  all: ['limits'] as const,
  lists: () => [...limitKeys.all, 'list'] as const,
  list: (params: LimitRuleListParams) => [...limitKeys.lists(), params] as const,
  details: () => [...limitKeys.all, 'detail'] as const,
  detail: (id: string) => [...limitKeys.details(), id] as const,
  merchants: (ruleId: string, search?: string) => [...limitKeys.all, 'merchants', ruleId, search] as const,
};

// ─── Query Hooks ────────────────────────────────────────────────────────────

/**
 * Hook para listar reglas de límites.
 */
export function useLimitRules(params: LimitRuleListParams) {
  return useQuery<LimitRuleListResponse, Error>({
    queryKey: limitKeys.list(params),
    queryFn: () => getLimitRules(params),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook para obtener detalle de una regla.
 */
export function useLimitRule(id: string | undefined) {
  return useQuery<LimitRule, Error>({
    queryKey: limitKeys.detail(id ?? ''),
    queryFn: () => getLimitRule(id!),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

// ─── Mutation Hooks ─────────────────────────────────────────────────────────

/**
 * Hook para crear una regla de límite.
 */
export function useCreateLimitRule() {
  const queryClient = useQueryClient();

  return useMutation<LimitRule, Error, CreateLimitRulePayload>({
    mutationFn: (payload) => createLimitRule(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: limitKeys.lists() });
    },
  });
}

/**
 * Hook para actualizar una regla de límite (incluye toggle active).
 */
export function useUpdateLimitRule() {
  const queryClient = useQueryClient();

  return useMutation<LimitRule, Error, { id: string; payload: UpdateLimitRulePayload }>({
    mutationFn: ({ id, payload }) => updateLimitRule(id, payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(limitKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: limitKeys.lists() });
    },
  });
}

/**
 * Hook para asignar merchant a regla.
 */
export function useAssignMerchant() {
  const queryClient = useQueryClient();

  return useMutation<LimitAssignment, Error, { ruleId: string; payload: AssignMerchantPayload }>({
    mutationFn: ({ ruleId, payload }) => assignMerchantToRule(ruleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: limitKeys.lists() });
    },
  });
}

/**
 * Hook para quitar asignación de merchant.
 */
export function useUnassignMerchant() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { ruleId: string; assignmentId: string }>({
    mutationFn: ({ ruleId, assignmentId }) => unassignMerchantFromRule(ruleId, assignmentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: limitKeys.lists() });
      queryClient.invalidateQueries({ queryKey: limitKeys.merchants(variables.ruleId) });
    },
  });
}

/**
 * Hook para obtener merchants con estado de asignación a una regla.
 */
export function useLimitRuleMerchants(ruleId: string | undefined, search?: string) {
  return useQuery<MerchantAssignmentSummary[], Error>({
    queryKey: limitKeys.merchants(ruleId ?? '', search),
    queryFn: () => getLimitRuleMerchants(ruleId!, search),
    enabled: Boolean(ruleId),
    staleTime: 30_000,
  });
}

/**
 * Hook para toggle de estado activo/inactivo de una regla.
 */
export function useToggleLimitRuleStatus() {
  const queryClient = useQueryClient();

  return useMutation<LimitRule, Error, string>({
    mutationFn: (id) => toggleLimitRuleStatus(id),
    onSuccess: (data, id) => {
      queryClient.setQueryData(limitKeys.detail(id), data);
      queryClient.invalidateQueries({ queryKey: limitKeys.lists() });
    },
  });
}
